/**
 * FlowDetail Component
 *
 * Detailed view of a flow with tabs for different sections.
 * Shows overview, diagram, variables, run history, and versions.
 *
 * @component
 */

import React, { useState, useMemo } from 'react';
import {
  ArrowLeft,
  Edit2,
  Copy,
  Trash2,
  Play,
  Pause,
  Monitor,
  Zap,
  Clock,
  Database,
  Radio,
  Activity,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronRight,
  GitBranch,
  History,
  Variable,
  LayoutGrid,
  FileText
} from 'lucide-react';
import {
  StandardButton,
  StandardCard,
  StandardDataTable,
  StandardModal,
  StandardLoadingState,
  StandardEmptyState,
  useToast
} from '../../standard';
import { flowAutomationStorage, FLOW_TYPE_DEFINITIONS } from '../../../services/flowAutomationStorage';
import type { Flow, FlowRun, FlowVersion, FlowVariable, FlowElement } from '../../../types/flowAutomation';
import { FlowCanvas } from './FlowCanvas';
import { FlowRunHistory } from './FlowRunHistory';
import { VariableManager } from './VariableManager';

// ============================================
// INTERFACES
// ============================================

interface FlowDetailProps {
  flow: Flow;
  runs: FlowRun[];
  onBack: () => void;
  onEdit: (flow: Flow) => void;
  onClone: (flow: Flow) => void;
  onDelete: (flow: Flow) => void;
  onActivate: (flow: Flow) => void;
  onDeactivate: (flow: Flow) => void;
  onRefreshRuns: () => void;
}

type TabId = 'overview' | 'diagram' | 'variables' | 'history' | 'versions';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

const getFlowTypeIcon = (type: string, size: number = 20) => {
  switch (type) {
    case 'screen_flow':
      return <Monitor size={size} className="text-blue-500" />;
    case 'auto_launched':
      return <Zap size={size} className="text-yellow-500" />;
    case 'scheduled':
      return <Clock size={size} className="text-purple-500" />;
    case 'record_triggered':
      return <Database size={size} className="text-green-500" />;
    case 'platform_event':
      return <Radio size={size} className="text-orange-500" />;
    default:
      return <Activity size={size} className="text-gray-500" />;
  }
};

const getStatusBadge = (status: string) => {
  const styles: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    inactive: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    invalid: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
  };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[status] || styles.draft}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const formatDate = (dateString: string | undefined) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDuration = (ms: number | undefined) => {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

// ============================================
// TABS CONFIGURATION
// ============================================

const TABS: Tab[] = [
  { id: 'overview', label: 'Overview', icon: <FileText size={16} /> },
  { id: 'diagram', label: 'Flow Diagram', icon: <LayoutGrid size={16} /> },
  { id: 'variables', label: 'Variables', icon: <Variable size={16} /> },
  { id: 'history', label: 'Run History', icon: <History size={16} /> },
  { id: 'versions', label: 'Versions', icon: <GitBranch size={16} /> }
];

// ============================================
// OVERVIEW TAB COMPONENT
// ============================================

interface OverviewTabProps {
  flow: Flow;
  runs: FlowRun[];
}

const OverviewTab: React.FC<OverviewTabProps> = ({ flow, runs }) => {
  const typeDef = FLOW_TYPE_DEFINITIONS.find(t => t.type === flow.type);

  const successRate = flow.runCount > 0
    ? Math.round((flow.successCount / flow.runCount) * 100)
    : 0;

  const avgDuration = useMemo(() => {
    const completedRuns = runs.filter(r => r.status === 'completed' && r.duration);
    if (completedRuns.length === 0) return 0;
    const total = completedRuns.reduce((sum, r) => sum + (r.duration || 0), 0);
    return total / completedRuns.length;
  }, [runs]);

  const lastRun = runs.length > 0 ? runs[0] : null;

  return (
    <div className="space-y-6">
      {/* Flow Info Card */}
      <StandardCard className="p-6">
        <div className="flex items-start gap-4">
          <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-xl">
            {getFlowTypeIcon(flow.type, 32)}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {flow.name}
              </h2>
              {getStatusBadge(flow.status)}
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {flow.description || 'No description provided'}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">Type</span>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {typeDef?.label || flow.type}
                </p>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">API Name</span>
                <p className="font-medium text-gray-900 dark:text-gray-100 font-mono text-xs">
                  {flow.apiName}
                </p>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Version</span>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  v{flow.version}
                </p>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Elements</span>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {flow.elements.length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </StandardCard>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StandardCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Activity size={20} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Runs</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {flow.runCount}
              </p>
            </div>
          </div>
        </StandardCard>

        <StandardCard className="p-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${
              successRate >= 90
                ? 'bg-green-100 dark:bg-green-900/30'
                : successRate >= 70
                ? 'bg-yellow-100 dark:bg-yellow-900/30'
                : 'bg-red-100 dark:bg-red-900/30'
            }`}>
              {successRate >= 90 ? (
                <CheckCircle size={20} className="text-green-600 dark:text-green-400" />
              ) : (
                <AlertCircle size={20} className={
                  successRate >= 70
                    ? 'text-yellow-600 dark:text-yellow-400'
                    : 'text-red-600 dark:text-red-400'
                } />
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Success Rate</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {successRate}%
              </p>
            </div>
          </div>
        </StandardCard>

        <StandardCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Clock size={20} className="text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Avg. Duration</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {formatDuration(avgDuration)}
              </p>
            </div>
          </div>
        </StandardCard>

        <StandardCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <History size={20} className="text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Last Run</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {formatDate(flow.lastRunDate)}
              </p>
            </div>
          </div>
        </StandardCard>
      </div>

      {/* Trigger Configuration */}
      <StandardCard className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Trigger Configuration
        </h3>

        {flow.type === 'scheduled' && flow.scheduleConfig && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Frequency</span>
              <p className="font-medium text-gray-900 dark:text-gray-100 capitalize">
                {flow.scheduleConfig.frequency}
              </p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Start Date</span>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {flow.scheduleConfig.startDate}
              </p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Start Time</span>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {flow.scheduleConfig.startTime}
              </p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Timezone</span>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {flow.scheduleConfig.timezone}
              </p>
            </div>
          </div>
        )}

        {flow.type === 'record_triggered' && flow.recordTriggerConfig && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Object</span>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {flow.recordTriggerConfig.objectType}
              </p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Trigger</span>
              <p className="font-medium text-gray-900 dark:text-gray-100 capitalize">
                {flow.recordTriggerConfig.triggerType.replace(/_/g, ' ')}
              </p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Execution</span>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {flow.recordTriggerConfig.runAsync ? 'Asynchronous' : 'Immediate'}
              </p>
            </div>
          </div>
        )}

        {(flow.type === 'screen_flow' || flow.type === 'auto_launched') && (
          <p className="text-gray-600 dark:text-gray-400">
            This flow is manually triggered or invoked from other processes.
          </p>
        )}

        {flow.type === 'platform_event' && flow.platformEventConfig && (
          <div className="text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Event Type</span>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {flow.platformEventConfig.eventType}
              </p>
            </div>
          </div>
        )}
      </StandardCard>

      {/* Variables Summary */}
      {flow.variables.length > 0 && (
        <StandardCard className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Variables ({flow.variables.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {flow.variables.map(v => (
              <div
                key={v.id}
                className="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm"
              >
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {v.name}
                </span>
                <span className="text-gray-500 dark:text-gray-400 ml-2">
                  ({v.type})
                </span>
                {v.isInput && (
                  <span className="ml-2 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs rounded">
                    Input
                  </span>
                )}
                {v.isOutput && (
                  <span className="ml-2 px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded">
                    Output
                  </span>
                )}
              </div>
            ))}
          </div>
        </StandardCard>
      )}

      {/* Last Run Info */}
      {lastRun && (
        <StandardCard className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Last Run
          </h3>
          <div className="flex items-center gap-4">
            <div className={`p-2 rounded-lg ${
              lastRun.status === 'completed'
                ? 'bg-green-100 dark:bg-green-900/30'
                : lastRun.status === 'failed'
                ? 'bg-red-100 dark:bg-red-900/30'
                : 'bg-yellow-100 dark:bg-yellow-900/30'
            }`}>
              {lastRun.status === 'completed' ? (
                <CheckCircle size={20} className="text-green-600 dark:text-green-400" />
              ) : lastRun.status === 'failed' ? (
                <XCircle size={20} className="text-red-600 dark:text-red-400" />
              ) : (
                <AlertCircle size={20} className="text-yellow-600 dark:text-yellow-400" />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className={`text-sm font-medium ${
                  lastRun.status === 'completed'
                    ? 'text-green-600 dark:text-green-400'
                    : lastRun.status === 'failed'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-yellow-600 dark:text-yellow-400'
                }`}>
                  {lastRun.status.charAt(0).toUpperCase() + lastRun.status.slice(1)}
                </span>
                <span className="text-gray-500 dark:text-gray-400">•</span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {formatDate(lastRun.startTime)}
                </span>
                {lastRun.duration && (
                  <>
                    <span className="text-gray-500 dark:text-gray-400">•</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {formatDuration(lastRun.duration)}
                    </span>
                  </>
                )}
              </div>
              {lastRun.errorMessage && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                  {lastRun.errorMessage}
                </p>
              )}
            </div>
          </div>
        </StandardCard>
      )}

      {/* Metadata */}
      <StandardCard className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Metadata
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">Created By</span>
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {flow.createdByName}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {formatDate(flow.createdDate)}
            </p>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Last Modified By</span>
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {flow.lastModifiedByName}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {formatDate(flow.lastModifiedDate)}
            </p>
          </div>
        </div>
      </StandardCard>
    </div>
  );
};

// ============================================
// VERSIONS TAB COMPONENT
// ============================================

interface VersionsTabProps {
  flow: Flow;
  onActivateVersion: (version: FlowVersion) => void;
}

const VersionsTab: React.FC<VersionsTabProps> = ({ flow, onActivateVersion }) => {
  const columns = [
    {
      key: 'version',
      label: 'Version',
      render: (_value: unknown, version: FlowVersion) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900 dark:text-gray-100">
            v{version.version}
          </span>
          {version.version === flow.version && (
            <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs rounded-full">
              Current
            </span>
          )}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (_value: unknown, version: FlowVersion) => getStatusBadge(version.status)
    },
    {
      key: 'createdBy',
      label: 'Created By',
      render: (_value: unknown, version: FlowVersion) => (
        <div className="text-sm">
          <p className="text-gray-900 dark:text-gray-100">{version.createdByName}</p>
          <p className="text-gray-500 dark:text-gray-400 text-xs">
            {formatDate(version.createdDate)}
          </p>
        </div>
      )
    },
    {
      key: 'activatedBy',
      label: 'Activated By',
      render: (_value: unknown, version: FlowVersion) => (
        version.activatedByName ? (
          <div className="text-sm">
            <p className="text-gray-900 dark:text-gray-100">{version.activatedByName}</p>
            <p className="text-gray-500 dark:text-gray-400 text-xs">
              {formatDate(version.activatedDate)}
            </p>
          </div>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">-</span>
        )
      )
    },
    {
      key: 'description',
      label: 'Description',
      render: (_value: unknown, version: FlowVersion) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {version.description || '-'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_value: unknown, version: FlowVersion) => (
        version.version !== flow.version && version.status !== 'active' ? (
          <StandardButton
            variant="secondary"
            size="sm"
            onClick={() => onActivateVersion(version)}
          >
            Activate
          </StandardButton>
        ) : null
      )
    }
  ];

  return (
    <div>
      {flow.versions.length === 0 ? (
        <StandardEmptyState
          title="No Version History"
          message="This flow only has one version."
        />
      ) : (
        <StandardCard>
          <StandardDataTable
            data={flow.versions}
            columns={columns}
          />
        </StandardCard>
      )}
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const FlowDetail: React.FC<FlowDetailProps> = ({
  flow,
  runs,
  onBack,
  onEdit,
  onClone,
  onDelete,
  onActivate,
  onDeactivate,
  onRefreshRuns
}) => {
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleActivateVersion = (version: FlowVersion) => {
    addToast({
      type: 'info',
      title: 'Version Activation',
      message: `Activating version ${version.version}... (Demo mode)`
    });
  };

  const handleDeleteConfirm = () => {
    onDelete(flow);
    setShowDeleteModal(false);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab flow={flow} runs={runs} />;
      case 'diagram':
        return (
          <FlowCanvas
            elements={flow.elements}
            readOnly={true}
            onElementClick={(element) => {
              addToast({
                type: 'info',
                title: element.label,
                message: `Type: ${element.type}`
              });
            }}
          />
        );
      case 'variables':
        return (
          <VariableManager
            variables={flow.variables}
            readOnly={true}
          />
        );
      case 'history':
        return (
          <FlowRunHistory
            flowId={flow.id}
            runs={runs}
            onRefresh={onRefreshRuns}
          />
        );
      case 'versions':
        return (
          <VersionsTab
            flow={flow}
            onActivateVersion={handleActivateVersion}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <StandardButton variant="ghost" onClick={onBack}>
            <ArrowLeft size={20} />
          </StandardButton>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
              {getFlowTypeIcon(flow.type, 24)}
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {flow.name}
                </h1>
                {getStatusBadge(flow.status)}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {FLOW_TYPE_DEFINITIONS.find(t => t.type === flow.type)?.label} • v{flow.version}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {(flow.type === 'screen_flow' || flow.type === 'auto_launched') && (
            <StandardButton
              variant="secondary"
              leftIcon={<Play size={16} />}
              onClick={() => {
                addToast({
                  type: 'info',
                  title: 'Run Flow',
                  message: 'Running flow... (Demo mode)'
                });
              }}
            >
              Run
            </StandardButton>
          )}
          <StandardButton
            variant="secondary"
            leftIcon={<Edit2 size={16} />}
            onClick={() => onEdit(flow)}
          >
            Edit
          </StandardButton>
          <StandardButton
            variant="secondary"
            leftIcon={<Copy size={16} />}
            onClick={() => onClone(flow)}
          >
            Clone
          </StandardButton>
          {flow.status === 'active' ? (
            <StandardButton
              variant="secondary"
              leftIcon={<Pause size={16} />}
              onClick={() => onDeactivate(flow)}
            >
              Deactivate
            </StandardButton>
          ) : flow.status !== 'invalid' && (
            <StandardButton
              variant="primary"
              leftIcon={<Play size={16} />}
              onClick={() => onActivate(flow)}
            >
              Activate
            </StandardButton>
          )}
          <StandardButton
            variant="ghost"
            className="text-red-500 hover:text-red-600"
            onClick={() => setShowDeleteModal(true)}
          >
            <Trash2 size={16} />
          </StandardButton>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex gap-1">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Delete Confirmation Modal */}
      <StandardModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Flow"
        size="sm"
      >
        <div className="p-4">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Are you sure you want to delete <strong>"{flow.name}"</strong>? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <StandardButton
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </StandardButton>
            <StandardButton
              variant="primary"
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeleteConfirm}
            >
              Delete Flow
            </StandardButton>
          </div>
        </div>
      </StandardModal>
    </div>
  );
};

export default FlowDetail;
